import {
  type AskedQuestion,
  campaignAnswersProblem,
  campaignDisplayLabel,
  centsFrom,
  formatMoney,
  type GivenAnswer,
} from '@app/shared'
import { Fragment } from 'react'
import { Ask, Choice, Field, MoneyInput, Question, Select } from '../ui'

/** What a donor has picked, per question, for one campaign. */
export type PickedAnswers = Record<string, string[]>

/** The value an optional pick-one question takes when it is left unanswered. */
const NO_ANSWER = ''

/** The picks as the checkout takes them: only questions the page asks. */
export function answersFor(
  questions: AskedQuestion[],
  picked: PickedAnswers | undefined,
): GivenAnswer[] {
  return questions.map((q) => ({
    questionId: q.id,
    optionIds: (picked?.[q.id] ?? []).filter((id) =>
      q.options.some((o) => o.id === id),
    ),
  }))
}

/** The key a page's own answers are held under, beside each companion's. */
export const OWN = ''

/**
 * Whether a gift asks anything, and the first question it still owes an
 * answer. Each campaign's questions bind its own gift: a companion with no
 * amount is not being given to, so nothing it asks stands in the way, and a
 * companion's is named, since two campaigns can ask alike.
 */
export function questionsStanding(
  own: AskedQuestion[],
  beside: Array<{
    shortId: string
    label: string
    period: string | null
    questions: AskedQuestion[]
  }>,
  picked: Record<string, PickedAnswers>,
): { asks: boolean; unanswered: string | null } {
  const unanswered =
    answersProblem(own, picked[OWN]) ??
    beside
      .map((l) => {
        const problem = answersProblem(l.questions, picked[l.shortId])
        return problem
          ? `${campaignDisplayLabel(l.label, l.period)}: ${problem}`
          : null
      })
      .find((p) => p !== null) ??
    null
  return {
    asks: own.length > 0 || beside.some((l) => l.questions.length > 0),
    unanswered,
  }
}

/** What still stands between these answers and the gift, or null. */
export function answersProblem(
  questions: AskedQuestion[],
  picked: PickedAnswers | undefined,
): string | null {
  return (
    campaignAnswersProblem(questions, answersFor(questions, picked))?.message ??
    null
  )
}

/**
 * Past this many answers a question is a list to search rather than a set to
 * compare, and a column of marks that long is a scroll region pretending to be
 * a question.
 */
const LONGEST_SET = 8

/**
 * A campaign's own questions, each its own set of choices with the question as
 * its name. Nothing is preselected: a default would be an answer nobody gave.
 * A pick-one question that may be skipped offers "No answer", since a set of
 * radios cannot be cleared once one is chosen.
 */
export function CampaignQuestions({
  questions,
  value,
  onValueChange,
}: {
  questions: AskedQuestion[]
  value: PickedAnswers | undefined
  onValueChange: (next: PickedAnswers) => void
}) {
  if (questions.length === 0) return null
  const set = (questionId: string, picks: string[]) =>
    onValueChange({ ...value, [questionId]: picks })
  return (
    <>
      {questions.map((q) => {
        const options = q.options.map((o) => ({ value: o.id, label: o.label }))
        const picks = value?.[q.id] ?? []
        if (options.length > LONGEST_SET) {
          return (
            <Field
              key={q.id}
              label={q.prompt}
              optional={!q.required}
              span="full"
            >
              {q.kind === 'several' ? (
                <Select
                  multiple
                  searchable
                  label={q.prompt}
                  values={picks}
                  onValuesChange={(next) => set(q.id, next)}
                  summary={
                    options
                      .filter((o) => picks.includes(o.value))
                      .map((o) => o.label)
                      .join(', ') || undefined
                  }
                  options={options}
                  placeholder="Choose"
                />
              ) : (
                <Select
                  searchable
                  label={q.prompt}
                  value={picks[0] ?? null}
                  onValueChange={(pick) => set(q.id, pick ? [pick] : [])}
                  options={
                    q.required
                      ? options
                      : [...options, { value: NO_ANSWER, label: 'No answer' }]
                  }
                  placeholder="Choose"
                />
              )}
            </Field>
          )
        }
        return q.kind === 'several' ? (
          <Choice
            key={q.id}
            multiple
            label={q.prompt}
            optional={!q.required}
            options={options}
            value={picks}
            onValueChange={(next) => set(q.id, next)}
          />
        ) : (
          <Choice
            key={q.id}
            label={q.prompt}
            optional={!q.required}
            options={
              q.required
                ? options
                : [...options, { value: NO_ANSWER, label: 'No answer' }]
            }
            value={
              value && q.id in value
                ? (value[q.id]?.[0] ?? NO_ANSWER)
                : undefined
            }
            onValueChange={(pick) => set(q.id, pick ? [pick] : [])}
          />
        )
      })}
    </>
  )
}

type Companion = {
  shortId: string
  label: string
  period: string | null
  tagline: string | null
  questions: AskedQuestion[]
}

/**
 * The campaigns a page offers beside its own gift, each an amount of its own
 * and each optional: the donor gives to as many as they choose in the one
 * payment, and each counts only its own. A companion's questions are asked
 * under its amount once one is typed, since they are about that gift.
 */
export function BesideGifts({
  companions,
  minCents,
  maxCents,
  typed,
  onTypedChange,
  picked,
  onPickedChange,
}: {
  companions: Companion[]
  minCents: number
  maxCents: number
  /** What is typed for each companion, by its address. */
  typed: Record<string, string>
  onTypedChange: (shortId: string, value: string) => void
  picked: Record<string, PickedAnswers>
  onPickedChange: (shortId: string, next: PickedAnswers) => void
}) {
  if (companions.length === 0) return null
  return (
    <Question ask="Also give, in the same payment">
      <Ask>Also give, in the same payment</Ask>
      {companions.map((x) => {
        const given = typed[x.shortId] ?? ''
        const cents = centsFrom(given)
        const out = cents !== null && (cents < minCents || cents > maxCents)
        return (
          <Fragment key={x.shortId}>
            <Field
              label={campaignDisplayLabel(x.label, x.period)}
              info={x.tagline ?? undefined}
              optional
              problem={
                out
                  ? `A gift is taken between ${formatMoney(minCents)} and ${formatMoney(maxCents)}.`
                  : null
              }
            >
              <MoneyInput
                value={given}
                min={minCents / 100}
                max={maxCents / 100}
                aria-invalid={out || undefined}
                onChange={(e) => onTypedChange(x.shortId, e.target.value)}
              />
            </Field>
            {cents !== null && cents > 0 && (
              <CampaignQuestions
                questions={x.questions}
                value={picked[x.shortId]}
                onValueChange={(next) => onPickedChange(x.shortId, next)}
              />
            )}
          </Fragment>
        )
      })}
    </Question>
  )
}
